import React from "react";

import { Box, ButtonBase, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
  AddRounded,
  DragIndicatorRounded,
  EditRounded,
  FastfoodRounded,
  MenuRounded,
  SaveRounded,
  SettingsRounded,
} from "@mui/icons-material";
import { arrayMove, List } from "react-movable";

import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { CafeteriaService } from "../../../config/services/CafeteriaService";
import { GreyPallet } from "../../../config/styles";
import { UtilComponents } from "../../../config/util/utilComponents";
import UtilFunctions from "../../../config/util/utilFunctions";
import useCafeteria from "../context";

import MainBorderButton from "../../../components/MainBorderButton";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";
import ModalWriteItem from "../components/ModalWriteItem";

import { CafeteriaFoodItemType } from "../../../types/database";

export default function DashboardTab() {
  const navigate = useNavigate();

  const MenuUpdate = CafeteriaService.Update();

  const { currentCafeteria, handleGetCafeterias } = useCafeteria();
  const { setShowSideMenuDrawer } = useMainState();

  const { pushAlert, showImage } = useModal();

  const [showModalEditItem, setShowModalEditItem] = React.useState<CafeteriaFoodItemType | null>(
    null
  );
  const [showModalCreateItem, setShowModalCreateItem] = React.useState(false);

  const [serviceOrderList, setServiceOrderList] = React.useState<CafeteriaFoodItemType[]>(
    (currentCafeteria?.menu
      ? UtilFunctions.sortObjectArrayBy(currentCafeteria.menu, "order", true)
      : []) as CafeteriaFoodItemType[]
  );

  const [changeOrder, setChangeOrder] = React.useState(false);

  //

  const handleUpdateMenu = async () => {
    await MenuUpdate.runRequest(currentCafeteria?.id || "", {
      menu: serviceOrderList.map((item, index) => ({ ...item, order: index })),
    })
      .then(() => {
        pushAlert({ type: "success", message: "Menu atualizado. " });
        handleGetCafeterias();
      })
      .catch((err) =>
        pushAlert({ type: "error", message: "Erro ao atualizar menu. " + err.message })
      );
  };

  const onCloseModalHandler = (reload?: boolean) => {
    setShowModalCreateItem(false);
    setShowModalEditItem(null);
    if (reload) handleGetCafeterias();
  };

  return (
    <Box>
      <PageHeader
        title="Lanchonete"
        leftIcon={<MenuRounded />}
        onLeftAction={() => setShowSideMenuDrawer(true)}
        rightIcon={<SettingsRounded />}
        onRightAction={() => navigate("/cafeteria/settings")}
      />

      <Box mb={1} mt={2}>
        <MainText size="body" color="black">
          Cardápio
        </MainText>
      </Box>

      {!!showModalEditItem && currentCafeteria && (
        <ModalWriteItem
          cafeteriaId={currentCafeteria?.id || ""}
          foodItem={showModalEditItem}
          menu={currentCafeteria?.menu}
          onClose={onCloseModalHandler}
        />
      )}

      {showModalCreateItem && currentCafeteria && (
        <ModalWriteItem
          cafeteriaId={currentCafeteria?.id || ""}
          menu={currentCafeteria?.menu}
          onClose={onCloseModalHandler}
        />
      )}

      <List
        lockVertically
        values={serviceOrderList}
        onChange={({ oldIndex, newIndex }) => {
          setServiceOrderList(arrayMove(serviceOrderList, oldIndex, newIndex));
        }}
        renderList={({ children, props }) => <div {...props}>{children}</div>}
        renderItem={({ value, props, isDragged, index }) => (
          <Box {...props} key={value.id} mb={2}>
            <Box
              sx={{
                opacity: value.available ? 1 : 0.5,
                alignItems: "center",
                display: "flex",
                gap: 1,
                backgroundColor: GreyPallet.Light,
                borderRadius: "15px",
                py: 2,
                px: 1,
              }}
            >
              <Box>
                <ButtonBase
                  onClick={() => !!value?.image && showImage(value.image.resource)}
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 19,
                    backgroundColor: GreyPallet.LightGhost,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: GreyPallet.Regular,
                    overflow: "hidden",
                  }}
                >
                  {value.image ? (
                    <img
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      src={value.image.resource}
                    />
                  ) : (
                    <FastfoodRounded sx={{ width: "20px" }} />
                  )}
                </ButtonBase>
              </Box>

              <Box sx={{ flex: 1, textDecoration: value.available ? "none" : "line-through" }}>
                <MainText size="caption">{value.name}</MainText>

                <MainText color="regular" size="small">
                  {value.description}
                </MainText>
              </Box>

              <Box sx={{ textAlign: "right", "& *": { whiteSpace: "nowrap" } }}>
                <MainText size="body" inline>
                  {UtilComponents.toPrice(value.price)}
                </MainText>
              </Box>

              {!changeOrder && (
                <Box>
                  <IconButton disabled={changeOrder} onClick={() => setShowModalEditItem(value)}>
                    <EditRounded />
                  </IconButton>
                </Box>
              )}

              <Box
                height={"100%"}
                data-movable-handle
                display={changeOrder ? "flex" : "none"}
                flexDirection={"column"}
                p={1}
              >
                <DragIndicatorRounded />
              </Box>
            </Box>
          </Box>
        )}
      />

      {currentCafeteria?.menu.length ? (
        <Box mb={2}>
          <MainBorderButton
            loading={MenuUpdate.isLoading}
            icon={changeOrder ? <SaveRounded /> : <EditRounded />}
            text={changeOrder ? "Salvar Ordem" : "Editar Ordem"}
            onClick={() => {
              changeOrder ? handleUpdateMenu() : setChangeOrder(!changeOrder);
            }}
          />
        </Box>
      ) : (
        <Box mb={1}>
          <MainText color="regular" size="small">
            Nenhum item no cardápio, clique no botão abaixo para adicionar.
          </MainText>
        </Box>
      )}

      <MainSolidButton
        text="Adicionar Item"
        icon={<AddRounded />}
        onClick={() => setShowModalCreateItem(true)}
      />
    </Box>
  );
}
